"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const is_browser_1 = __importDefault(require("./utils/is-browser"));
const types_1 = require("./types");
const constants_1 = require("./constants");
const get_client_id_1 = __importDefault(require("./utils/get-client-id"));
const get_date_string_1 = __importDefault(require("./utils/get-date-string"));
const instance_helper_1 = require("./utils/instance-helper");
const send_events_1 = require("./api/send-events");
const cache_1 = require("./utils/cache");
const api_1 = require("./api/api");
const session_1 = require("./utils/session");
const datetime_1 = require("./utils/datetime");
const browser_online_status_subscription_1 = require("./browser-online-status-subscription");
const defaultInstance = {
    // Session
    tenantId: '',
    sessionId: '',
    sessionCreated: '',
    environment: '',
    dataResidency: types_1.DataResidencyEnum.EU,
    country: '',
    locationAccuracy: undefined,
    latitude: undefined,
    longitude: undefined,
    spaceId: '',
    appId: '',
    appVersion: undefined,
    installationId: '',
    installationVersion: undefined,
    deviceId: null,
    clientId: null,
    accessId: '',
    accessToken: '',
    // Client Specific
    clientUserAgent: is_browser_1.default ? navigator === null || navigator === void 0 ? void 0 : navigator.userAgent : null,
    clientCreated: null,
    // Internal
    lastActivity: '',
};
class Session {
    constructor(initParams) {
        this.instance = defaultInstance;
        this.maxSessionLastActivityMins = constants_1.DEFAULT_MAX_SESSION_LAST_ACTIVITY_MINS;
        this.isOnline = false;
        this.init = (initParams) => __awaiter(this, void 0, void 0, function* () {
            if (this.instance.sessionId) {
                console.error(constants_1.INIT_ONCE_ERROR_MESSAGE);
            }
            else {
                browser_online_status_subscription_1.getOnlineStatusSubscription().subscribe((nextOnline) => {
                    this.isOnline = nextOnline;
                });
                const missingParams = instance_helper_1.getMissingRequiredInstanceParams(initParams);
                if (missingParams.length) {
                    const errorMessage = `${constants_1.MESSAGE_PREFIX} missing or invalid init params ${missingParams.join(', ')}`;
                    console.error(errorMessage);
                    throw new Error(errorMessage);
                }
                this.instance = Object.assign(Object.assign({}, this.instance), initParams);
                this.maxSessionLastActivityMins =
                    typeof initParams.createNewSessionAfterLastActivityMins === 'number'
                        ? initParams.createNewSessionAfterLastActivityMins
                        : this.maxSessionLastActivityMins;
                const clientInfo = get_client_id_1.default(this.instance);
                this.instance.clientId = clientInfo.clientId;
                this.instance.clientCreated = clientInfo.clientCreated;
                let sessionId = initParams.sessionId ? initParams.sessionId : '';
                let sessionCreated = '';
                let isSessionFromCache = false;
                if (sessionId) {
                    sessionCreated = get_date_string_1.default();
                    if (is_browser_1.default) {
                        session_1.cacheSession(sessionId, sessionCreated);
                    }
                }
                else {
                    const generatedSession = session_1.generateSession({
                        maxSessionLastActivityMins: this.maxSessionLastActivityMins,
                        forceCreateNewSession: typeof initParams.useValidCachedSessionOnInit === 'boolean'
                            ? !initParams.useValidCachedSessionOnInit
                            : false,
                    });
                    sessionId = generatedSession.sessionId;
                    sessionCreated = generatedSession.sessionCreated;
                    isSessionFromCache = generatedSession.isFromCache;
                }
                this.instance.sessionId = sessionId;
                this.instance.sessionCreated = sessionCreated;
                this.updateLastActivity();
                this.startSessionTracker();
                return Promise.all([
                    ...(isSessionFromCache
                        ? []
                        : [
                            this.sendOrCacheData(types_1.DataRequestTypeEnum.SESSION, instance_helper_1.getSessionFromInstance(this.instance)),
                        ]),
                    ...(isSessionFromCache ? [] : [this.sendAppStart()]),
                    ...(!this.instance.clientId
                        ? []
                        : [
                            this.sendOrCacheData(types_1.DataRequestTypeEnum.CLIENT, {
                                tenantId: this.instance.tenantId,
                                clientId: this.instance.clientId,
                                clientCreated: this.instance.clientCreated,
                                clientUserAgent: this.instance.clientUserAgent,
                                dataResidency: this.instance.dataResidency,
                                country: this.instance.country,
                                latitude: this.instance.latitude,
                                longitude: this.instance.longitude,
                                locationAccuracy: this.instance.locationAccuracy,
                            }),
                            this.identifyContact({
                                interaction: false,
                                identityType: types_1.IdentityTypeEnum.CLIENT_ID,
                                contact: this.instance.clientId,
                            }),
                        ]),
                ]);
            }
        });
        this.createSession = () => __awaiter(this, void 0, void 0, function* () {
            if (!instance_helper_1.isInstanceInitialized(this.instance)) {
                throw new Error(constants_1.CREATE_SESSION_ERROR_INIT_MESSAGE);
            }
            const { sessionId, sessionCreated } = session_1.generateSession({
                forceCreateNewSession: true,
                maxSessionLastActivityMins: this.maxSessionLastActivityMins,
            });
            this.instance.sessionId = sessionId;
            this.instance.sessionCreated = sessionCreated;
            this.updateLastActivity();
            return this.sendOrCacheData(types_1.DataRequestTypeEnum.SESSION, instance_helper_1.getSessionFromInstance(this.instance));
        });
        this.getInstanceProps = () => {
            return this.instance;
        };
        this.sendOrCacheData = (type, data) => __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this.isOnline && this.instance.clientUserAgent) {
                    throw new Error('Currently offline');
                }
                let sendFunction = send_events_1.getCallbackSendFunction(type);
                if (!sendFunction) {
                    throw new Error('Invalid data request type');
                }
                const response = yield sendFunction(data);
                if (this.instance.clientUserAgent && cache_1.cache && cache_1.cache.length) {
                    cache_1.flushCacheOnce(this.sendOrCacheData);
                }
                return response;
            }
            catch (err) {
                if (this.instance.clientUserAgent && cache_1.cache) {
                    const expectsResponse = !!data.expectsResponse;
                    delete data.expectsResponse;
                    cache_1.cacheData(type, data);
                    if (expectsResponse) {
                        throw new Error(err);
                    }
                }
                else {
                    throw err;
                }
            }
        });
        this.trackEvent = (event) => __awaiter(this, void 0, void 0, function* () {
            if (!instance_helper_1.isInstanceInitialized(this.instance)) {
                throw new Error(constants_1.TRACK_EVENT_ERROR_INIT_MESSAGE);
            }
            this.updateLastActivity();
            return this.sendOrCacheData(types_1.DataRequestTypeEnum.EVENT, Object.assign({ tenantId: this.instance.tenantId, spaceId: this.instance.spaceId, eventTime: get_date_string_1.default(), dataResidency: this.instance.dataResidency, sessionId: this.instance.sessionId, clientId: this.instance.clientId }, event));
        });
        this.startSessionTracker = () => {
            setInterval(() => {
                const isExpiredSession = datetime_1.getDifInMins(get_date_string_1.default(), this.instance.lastActivity) >=
                    this.maxSessionLastActivityMins;
                if (isExpiredSession) {
                    this.createSession();
                }
            }, 60000);
        };
        this.updateLastActivity = () => {
            const lastActivityCache = session_1.setLastActivityCache(get_date_string_1.default());
            this.instance.lastActivity = lastActivityCache || get_date_string_1.default();
        };
        /**
         * Custom event
         */
        this.sendCustomEvent = (event) => __awaiter(this, void 0, void 0, function* () {
            return this.trackEvent(event);
        });
        /**
         * Identifying the user based on a known identifyer
         */
        this.identifyContact = ({ interaction, identityType, contact, customIdentityType, }) => {
            if (identityType === types_1.IdentityTypeEnum.OTHER && !customIdentityType) {
                throw new Error('Custom identity type is required');
            }
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CONTACT_IDENTIFY,
                interaction,
                str1: identityType === types_1.IdentityTypeEnum.OTHER ? customIdentityType : identityType,
                str2: contact,
            });
        };
        /**
         * Identifying the user based on a known identifyer and get the contactId as a response
         */
        this.identifyContactAndGetId = ({ interaction, identityType, contact, customIdentityType, }) => {
            if (identityType === types_1.IdentityTypeEnum.OTHER && !customIdentityType) {
                throw new Error('Custom identity type is required');
            }
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CONTACT_IDENTIFY,
                interaction,
                str1: identityType === types_1.IdentityTypeEnum.OTHER ? customIdentityType : identityType,
                str2: contact,
                expectsResponse: true,
            }).then((res) => res.contactId);
        };
        /**
         * Identifying the customer based on a known identifyer
         */
        this.sendContactMetadata = ({ interaction, str1, str2, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CONTACT_METADATA,
                interaction,
                str1,
                str2,
            });
        };
        /**
         * Information about the user, return from computer vision or selected by a user in the UI
         * Note, the below method will update the Contact Metadata but it will also log the values for analytics purposes.
         * If CONTACT_METADATA event is used directly, then the value is not logged for reports
         */
        this.detectMood = ({ mood, certainty, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.DETECT_MOOD,
                interaction: true,
                str1: mood,
                int1: certainty,
            });
        };
        /**
         * Information about the user, returned from computer vision, 3rd party services, or selected by a user in the UI
         * Note, the below method will update the Contact Metadata but it will also log the values for analytics purposes.
         * If CONTACT_METADATA event is used directly, then the value is not logged for reports
         */
        this.detectAge = ({ interaction, // If automatically detected with computer vision then FALSE. If user pick from an option in UI then TRUE
        targetAge, ageRange, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.DETECT_AGE,
                interaction,
                int1: targetAge,
                int2: ageRange,
            });
        };
        /**
         * Information about the user, returned from computer vision, 3rd party services, or selected by a user in the UI
         * Note, the below method will update the Contact Metadata but it will also log the values for analytics purposes.
         * If CONTACT_METADATA event is used directly, then the value is not logged for reports
         */
        this.detectGender = ({ interaction, // If automatically detected with computer vision then FALSE. If user pick from an option in UI then TRUE
        gender, certainty, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.DETECT_MOOD,
                interaction,
                str1: gender,
                int1: certainty,
            });
        };
        /**
         * Browsing products under a category
         */
        this.sendCategoryView = ({ categoryId }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CATEGORY_VIEW,
                categoryId,
                interaction: true,
            });
        };
        /**
         * Viewing a specific product page
         */
        this.sendProductView = ({ productId }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.PRODUCT_VIEW,
                productId,
                interaction: true,
            });
        };
        /**
         * Viewing a specific fashion look, which is an array of products
         */
        this.sendLookView = ({ lookId }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.LOOK_VIEW,
                str1: lookId,
                interaction: true,
            });
        };
        /**
         * Adding a product to the cart
         */
        this.sendCartAdd = ({ productId, quantity, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CART_ADD,
                productId,
                interaction: true,
                int1: quantity,
            });
        };
        /**
         * Viewing the cart page
         */
        this.sendCartView = () => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CART_VIEW,
                interaction: true,
            });
        };
        /**
         * Removing a product from the cart
         */
        this.sendCartRemove = ({ productId, quantity, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CART_REMOVE,
                interaction: true,
                productId,
                int1: quantity,
            });
        };
        /**
         * Clear the cart
         */
        this.sendCartClear = () => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CART_CLEAR,
                interaction: true,
            });
        };
        /**
         * Checkout step before payment
         */
        // TODO: add more checkout information to this event
        this.sendCheckout = () => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CHECKOUT,
                interaction: true,
            });
        };
        /**
         * Payment success
         */
        this.sendPurchase = ({ transactionId, currencyCode, revenue, tax, shipping, coupon, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.PURCHASE,
                interaction: true,
                str1: transactionId,
                str2: currencyCode,
                str3: coupon,
                int1: revenue,
                int2: tax,
                int3: shipping,
            });
        };
        /**
         * Everytime gridapp loads
         */
        this.sendAppStart = () => this.trackEvent({
            eventType: types_1.EventTypeEnum.APP_START,
            interaction: false,
        });
        /**
         * Viewing a generic piece of content, equivalent to a "pageview" in old school web analytics
         */
        this.sendContentView = ({ title, url, localizedTitle, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.CONTENT_VIEW,
                interaction: true,
                str1: title,
                str2: url,
                str3: localizedTitle,
            });
        };
        /**
         * Searching a product, category, or anything in the app
         */
        this.sendSearch = ({ searchQueryString }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.SEARCH,
                interaction: true,
                str1: searchQueryString,
            });
        };
        /**
         * Searching a product, category, or anything in the app
         */
        this.sendSearchClear = () => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.SEARCH_CLEAR,
                interaction: true,
            });
        };
        /**
         * User's rate of the experience
         */
        this.sendRating = ({ rating, interactionDelay, comment, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.RATING,
                interaction: true,
                int1: rating,
                int2: interactionDelay,
                str1: comment,
            });
        };
        /**
         * User's feedback of the experience
         */
        this.sendFeedback = ({ feedback, rating, }) => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.FEEDBACK,
                interaction: true,
                str1: feedback,
            });
        };
        this.sendQrScan = () => {
            return this.trackEvent({
                eventType: types_1.EventTypeEnum.QR_RUN,
                interaction: true,
            });
        };
        /**
         * Setting state key, value and expiry duration (in ms)
         */
        this.setState = ({ key, value, expiryDuration, }) => {
            return this.trackEvent({
                eventType: (key || '').toUpperCase(),
                interaction: false,
                str1: typeof value === 'string' ? value : undefined,
                int1: typeof value === 'number' ? value : undefined,
                stateful: true,
                public: expiryDuration,
            });
        };
        this.getSessionState = () => {
            return api_1.getSessionState({
                tenantId: this.instance.tenantId,
                sessionId: this.instance.sessionId,
                dataResidency: this.instance.dataResidency,
            });
        };
        this.getSpaceState = () => {
            return api_1.getSpaceState({
                tenantId: this.instance.tenantId,
                spaceId: this.instance.spaceId,
                dataResidency: this.instance.dataResidency,
                accessId: this.instance.accessId,
                accessToken: this.instance.accessToken,
            });
        };
        this.getSpaceStateRows = () => {
            return api_1.getSpaceStateRows({
                tenantId: this.instance.tenantId,
                sessionId: this.instance.sessionId,
                dataResidency: this.instance.dataResidency,
                accessId: this.instance.accessId,
                accessToken: this.instance.accessToken,
            });
        };
        this.subscribeSessionState = (callback) => {
            return api_1.subscribeSessionState({
                dataResidency: this.instance.dataResidency,
                sessionId: this.instance.sessionId,
                callback,
            });
        };
        this.subscribeSessionEvent = (callback) => {
            return api_1.subscribeSessionEvent({
                dataResidency: this.instance.dataResidency,
                sessionId: this.instance.sessionId,
                callback,
            });
        };
        this.subscribeSpaceState = (callback) => {
            return api_1.subscribeSpaceState({
                spaceId: this.instance.spaceId,
                dataResidency: this.instance.dataResidency,
                accessId: this.instance.accessId,
                accessToken: this.instance.accessToken,
                callback,
            });
        };
        this.subscribeSpaceEvent = (callback) => {
            return api_1.subscribeSpaceEvent({
                spaceId: this.instance.spaceId,
                dataResidency: this.instance.dataResidency,
                accessId: this.instance.accessId,
                accessToken: this.instance.accessToken,
                callback,
            });
        };
        this.getContactId = () => {
            return api_1.getContact({
                tenantId: this.instance.tenantId,
                sessionId: this.instance.sessionId,
                dataResidency: this.instance.dataResidency,
            });
        };
        if (initParams) {
            this.init(initParams);
        }
    }
}
exports.default = Session;
